import { intersperse, SqlExprAst, sqlExprMacro } from './sql-ast';
import _ from 'lodash';
import type { AST } from '../ast';
import { Assert } from '@cotera/utilities';
import { Expression } from '../builder/expression';
import { And, Constant, If, Not, Or } from '../builder/utilities';
import { isFunctionIdentifier } from '../ast/func-identifier';
import { Intrinsics } from '../intrinsics';
import { Max, Min } from '../builder/aggregate';

export type FnCompilationRule = (args: readonly AST.ExprIR[]) => SqlExprAst;

type FnCompilationRuleBuilder = (op: string) => FnCompilationRule;

const infix: FnCompilationRuleBuilder = (op) => (args) =>
  sqlExprMacro`(${intersperse(args as SqlExprAst, [` ${op} `])})`;

const infixAlias =
  (alias: string): FnCompilationRuleBuilder =>
  (_op) =>
    infix(alias);
const functionCallAlias =
  (alias: string): FnCompilationRuleBuilder =>
  (_op) =>
    functionCall(alias);

const prefix: FnCompilationRuleBuilder = (op) => (args) =>
  sqlExprMacro`(${op} ${args})`;

const functionCall: FnCompilationRuleBuilder = (op) => (args) =>
  sqlExprMacro`${op}(${intersperse(args as SqlExprAst, [', '])})`;

const interpreter: FnCompilationRuleBuilder = (op) => (args) => {
  Assert.assert(isFunctionIdentifier(op));

  const result = Expression.fromAst({
    t: 'function-call',
    op,
    args,
  }).evaluate();

  return sqlExprMacro`${Constant(result).ir()}`;
};

const infixEquality: FnCompilationRuleBuilder = (op) => {
  return (args) => {
    Assert.assert(args.length === 2, 'Equality is binary');
    const arg0 = Expression.fromAst(args[0]!);
    const arg1 = Expression.fromAst(args[1]!);

    // If we're comparing two structs, then we need to rewrite the
    // comparison into a pariwise copmarison of all of the struct fields.
    if (arg0.ty.ty.k === 'struct' && arg1.ty.ty.k === 'struct') {
      const arg0Fields = arg0.ty.ty.fields;
      const arg1Fields = arg1.ty.ty.fields;

      // This is covered by the type checker, but it's worth asserting here.
      const fields = Object.keys(arg0Fields);
      Assert.assert(
        _.isEqual(new Set(fields), new Set(Object.keys(arg1Fields))),
        'Structs must have identical fields to be compared. This is an error in the type checker.'
      );

      // We're ready to compare - we're just going to rewrite the expression
      // into the conjunction of the individual field comparisons, and pass
      // the result back into the whole compilation process again.
      const expr = And(
        ...fields.map((field) => arg0.getField(field).eq(arg1.getField(field)))
      );

      return sqlExprMacro`${op === 'eq' ? expr.ir() : Not(expr).ir()}`;
    }

    const sqlOp = { eq: '=', neq: '!=' }[op]!;
    return sqlExprMacro`(${intersperse(args as SqlExprAst, [` ${sqlOp} `])})`;
  };
};

const minMaxRule: FnCompilationRuleBuilder = (op) => (args) => {
  // We special case max/min to handle booleans. Certain warehouses don't
  // support max/min on booleans, so we provide a well defined answer in the
  // era abstract machine
  const [targetIr, ...rest] = args;
  Assert.assert(targetIr !== undefined && rest.length === 0);
  const target = Expression.fromAst(targetIr);

  return target.implements('boolean')
    ? // This looks weird, but the general idea is to use intergers to represent
      // the boolean, but still preserve nulls through the If
      [
        (op === 'max' ? Max : Min)(
          If(target, { then: 1, else: If(target.not(), { then: 0 }) })
        )
          .eq(1)
          .ir(),
      ]
    : functionCall(op)(args);
};

export const FUNCTION_SQL_RULES: Record<
  AST.FunctionIdentifier,
  FnCompilationRuleBuilder | 'override'
> = {
  one_of: (_op) => (args) => {
    const [target, matchers] = args;
    Assert.assert(
      target !== undefined && matchers !== undefined,
      'Type checker assured this'
    );

    const targetExpr = Expression.fromAst(target);
    if (matchers.t === 'make-array') {
      const expr =
        matchers.elements.length === 0
          ? Constant(false)
          : Or(
              ...matchers.elements.map((ir) =>
                Expression.fromAst(ir).eq(targetExpr)
              )
            );

      return sqlExprMacro`${expr.ir()}`;
    } else if (
      matchers.t === 'function-call' &&
      matchers.op === 'empty_array_of'
    ) {
      return sqlExprMacro`${Constant(false).ir()}`;
    } else {
      throw new Error('Type checker logic bug');
    }
  },
  remove_prefix: (_op) => (args) => {
    const [target, prefix, ...rest] = args;

    Assert.assert(
      target !== undefined && prefix !== undefined && rest.length === 0
    );

    return sqlExprMacro`CASE WHEN ${prefix} IS NOT NULL THEN REGEXP_REPLACE(${target}, '^' || ${prefix}, '') END`;
  },
  remove_suffix: (_op) => (args) => {
    const [target, suffix, ...rest] = args;

    Assert.assert(
      target !== undefined && suffix !== undefined && rest.length === 0
    );

    return sqlExprMacro`CASE WHEN ${suffix} IS NOT NULL THEN REGEXP_REPLACE(${target}, ${suffix} || '$', '') END`;
  },
  starts_with: (_op) => (args) => {
    const [target, matcher] = args;

    Assert.assert(target !== undefined && matcher !== undefined);
    const val = Intrinsics.startsWith(
      Expression.fromAst(target),
      Expression.fromAst(matcher)
    );
    return sqlExprMacro`${val.ir()}`;
  },
  ends_with: (_op) => (args) => {
    const [target, matcher] = args;
    Assert.assert(target !== undefined && matcher !== undefined);
    const val = Intrinsics.endsWith(
      Expression.fromAst(target),
      Expression.fromAst(matcher)
    );
    return sqlExprMacro`${val.ir()}`;
  },
  substring: (_op) => (args) => {
    const [target, start, length] = args;
    Assert.assert(
      target !== undefined && start !== undefined && length !== undefined
    );
    return sqlExprMacro`case when ${start} > 0 then substring(${target}, ${start}, ${length}) end`;
  },
  category_at: (_op) => (args) => {
    const [target, at, ...rest] = args;
    Assert.assert(
      target !== undefined && at !== undefined && rest.length === 0
    );
    const val = Intrinsics.categoryAt(
      Expression.fromAst(target),
      Expression.fromAst(at)
    );
    return [val.ir()];
  },
  category_concat: (_op) => (args) => {
    const [lhs, rhs, ...rest] = args;
    Assert.assert(lhs !== undefined && rhs !== undefined && rest.length === 0);
    return [
      Intrinsics.categoryConcat(
        Expression.fromAst(lhs),
        Expression.fromAst(rhs)
      ).ir(),
    ];
  },
  date_fmt: (_op) => (args) => {
    const [target, fmt] = args;

    Assert.assert(
      target !== undefined &&
        fmt !== undefined &&
        fmt.t === 'scalar' &&
        typeof fmt.val === 'string'
    );

    const expr = Intrinsics.dateFmt(Expression.fromAst(target), fmt.val);
    Assert.assert(expr.isOk(), 'Type checker assures this');
    return sqlExprMacro`${expr.value.ir()}`;
  },
  lower: functionCall,
  upper: functionCall,
  like: infix,
  format: (_op) => (args) => [Intrinsics.format(args)],
  format_nullable: (_op) => (args) => [Intrinsics.format_nullable(args)],
  concat: (_op) => (args) =>
    sqlExprMacro`(${intersperse(
      args.map((arg) => sqlExprMacro`(${arg})`),
      [' || ']
    )})`,
  replace: functionCall,
  md5: functionCall,
  length: functionCall,
  add: infixAlias('+'),
  sub: infixAlias('-'),
  mul: infixAlias('*'),
  div: infixAlias('/'),
  to_the_power_of: infixAlias('^'),
  split_part: functionCall,
  round: functionCall,
  abs: functionCall,
  ln: functionCall,
  log_2: 'override',
  log_10: 'override',
  floor: functionCall,
  ceil: functionCall,
  cosine_distance: 'override',
  and: infix,
  or: infix,
  eq: infixEquality,
  neq: infixEquality,
  gt: infixAlias('>'),
  gte: infixAlias('>='),
  lt: infixAlias('<'),
  lte: infixAlias('<='),
  now: functionCall,
  not: prefix,
  is_null:
    (_op) =>
    ([arg]) =>
      sqlExprMacro`(${arg!} is null)`,
  is_not_null:
    (_op) =>
    ([arg]) =>
      sqlExprMacro`(not (${arg!} is null))`,
  is_numeric_string: 'override',
  gen_random_uuid: functionCall,
  random: functionCall,
  sum: functionCall,
  count: functionCall,
  count_distinct:
    (_op) =>
    ([target]) =>
      sqlExprMacro`count(distinct ${target!})`,
  tag:
    (_op) =>
    ([target, _tag]) =>
      sqlExprMacro`${target!}`,
  impure:
    (_op) =>
    ([target]) =>
      sqlExprMacro`${target!}`,
  avg: functionCall,
  min: minMaxRule,
  max: minMaxRule,
  string_agg: functionCall,
  date_diff: 'override',
  date_add: 'override',
  date_trunc: 'override',
  date_part: 'override',
  date_to_timestamp: 'override',
  nan: 'override',
  is_nan: 'override',
  null_if: functionCallAlias('nullif'),
  coalesce: functionCall,
  corr: functionCall,
  array_agg: 'override',
  percentile_cont: 'override',
  percentile_disc: 'override',
  stddev_samp: functionCall,
  stddev_pop: functionCall,
  get_from_record: 'override',
  null_of: interpreter,
  type_of: interpreter,
  empty_array_of: 'override',
};
